import { ReactElement, useEffect } from 'react'
import { useMediaQuery } from 'react-responsive'
import { useAppDispatch, useAppSelector } from './redux/hooks'
import BasisTypesPage from './components/basistypes'
import { CalendarPage } from './components/calendar'
import { ErrorBoundary } from 'react-error-boundary'
import { getIsServicePickerOpen } from './redux/reducers/calendarSettings'
import { Container, ErrorBlock, Grid } from '@raskrask-dk/rr-components'
import {
    AddressState,
    CalendarState,
    BasisState,
    DetailsState,
    CompanyLeadState,
    CompanyProductPickState,
    CheckoutState,
    PackageState,
    MoveBookingState,
    ErrorState,
    NotAvailableState,
    SuggestionMapper,
    AddressActions,
} from '@raskrask-dk/bookingflow-state-machine'
import {
    setBookingId,
    setFlow,
    setIsMobile,
} from './redux/reducers/appSettings'
import AddressStep from './components/address/page'
import CheckoutPage from './components/checkout'
import {
    initiateBookingWithKlippeKort,
    initiateRebooking,
    useBFSM,
    useBFSMState,
} from './contexts/bfsmContext'
import { useTranslation } from 'react-i18next'
import { getErrorHandler } from './services/errorHandler'
import CompanyProductPickPage from './components/companyProduct'
import CompanyLeadPage from './components/companylead'
import HeaderPage from './components/header'
import { ModifyBookingPage } from './components/modifybooking'
import { useParams } from 'react-router-dom'
import { useBackEvents } from './useBackEvents'
import { useTracking } from './useTracking'
import { KlippeKortPage } from './components/klippekort'
import ErrorPage from './components/error'
import NoPartnerAvailablePage from './components/error/nopartneravailable'
import { useQuery } from './services/hooks/useQuery'
import DetailsPage from './components/details/page'

const App = (): ReactElement => {
    const [BFSM, setBFSM] = useBFSM()
    const currentState = useBFSMState()
    const dispatch = useAppDispatch()
    const isMobileView = useMediaQuery({ maxWidth: 640 })
    const { t } = useTranslation()
    const errorHandler = getErrorHandler()

    const { bookingId, flow } = useParams()
    const queries = useQuery()
    useBackEvents(BFSM)
    useTracking(BFSM)

    const isServicePickerOpen = useAppSelector((state) =>
        getIsServicePickerOpen(state)
    )

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [currentState, isServicePickerOpen])

    useEffect(() => {
        if (flow) {
            if (flow === 'bookwithklippe') {
                const values = Object.fromEntries(queries.entries())
                new SuggestionMapper().fromAny(values).then((suggestions) => {
                    BFSM.addSuggestions = suggestions // address, basisTypeId, customerType, goalId, serviceTypeId, partnerId, customerName, customerEmail, customerPhone
                    initiateBookingWithKlippeKort(BFSM, suggestions, dispatch)
                })
                dispatch(setFlow(flow))
            } else {
                if (bookingId && flow === 'rebook') {
                    initiateRebooking(BFSM, bookingId, dispatch)
                } else if (bookingId && flow === 'movebook') {
                    setBFSM('MoveBooking')
                } else if (bookingId && flow === 'cancelbook') {
                    setBFSM('CancelBooking')
                }

                dispatch(setFlow(flow))
                dispatch(setBookingId(bookingId))
            }
            history.pushState({}, document.title, '/')
        } else {
            const values = Object.fromEntries(queries.entries())
            new SuggestionMapper().fromAny(values).then((suggestions) => {
                BFSM.addSuggestions = suggestions // address + customer details

                const addrNext = new AddressActions.Next(
                    BFSM.currentState as unknown as AddressState,
                    suggestions.address
                )
                if (addrNext.isValid()) {
                    BFSM.doAction(AddressActions.Next, suggestions.address)
                }
            })
        }
    }, [bookingId, flow, queries])

    useEffect(() => {
        dispatch(setIsMobile(isMobileView))
    }, [isMobileView])

    const showState = () => {
        switch (currentState.constructor) {
            case AddressState:
                return <AddressStep />
            case BasisState:
                return <BasisTypesPage />
            case CompanyLeadState:
                return <CompanyLeadPage />
            case CompanyProductPickState:
                return <CompanyProductPickPage />
            case PackageState:
                return <KlippeKortPage />
            case CalendarState:
                return <CalendarPage />
            case MoveBookingState:
                return <ModifyBookingPage />
            case DetailsState:
                return <DetailsPage />
            case CheckoutState:
                return <CheckoutPage />
            case NotAvailableState:
                return <NoPartnerAvailablePage />
            case ErrorState:
                return <ErrorPage />
            default:
                return (
                    <Container>
                        <ErrorBlock errorText={t('common:error.default')} />
                    </Container>
                )
        }
    }

    return (
        <Grid gap={16}>
            <HeaderPage />
            <ErrorBoundary
                FallbackComponent={ErrorPage}
                onError={(error) => errorHandler(error, BFSM)}
                onReset={() => console.log('runs reset')}
            >
                {showState()}
            </ErrorBoundary>
        </Grid>
    )
}

export default App
